import React, { useState, useEffect } from 'react'
import { FileText } from 'lucide-react'

const HistoryAndGatepass = () => {
  const [gatePasses, setGatePasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filterStatus, setFilterStatus] = useState('all') // all, issued, pending, expired
  const [searchTerm, setSearchTerm] = useState('')

  // Mock data for now - replace with actual API call
  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setGatePasses([]) // Empty for now
      setLoading(false)
    }, 1000)
  }, [])

  const filteredGatePasses = gatePasses.filter((gatePass) => {
    // Filter by status
    if (filterStatus !== 'all') {
      const status = gatePass.status?.toLowerCase() || gatePass.gatePassStatus?.toLowerCase()
      if (status !== filterStatus) {
        return false
      }
    }

    // Filter by search term (lot number, vehicle name, or gate pass number)
    if (searchTerm && searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase().trim()
      const lotNumber = gatePass?.vehicle?.lotNumber || gatePass?.lotNumber || ''
      const vehicleName = gatePass?.vehicle?.name || gatePass?.vehicleName || ''
      const gatePassNumber = gatePass?.gatePassNumber || gatePass?.id?.toString() || ''

      return (
        lotNumber.toLowerCase().includes(searchLower) ||
        vehicleName.toLowerCase().includes(searchLower) ||
        gatePassNumber.toLowerCase().includes(searchLower)
      )
    }

    return true
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">Error: {error}</div>
        <button
          onClick={() => window.location.reload()}
          className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="bg-[#FFF9F9] rounded-lg sm:rounded-xl shadow-md">
      {/* Header */}
      <div className="bg-[#fcfcfc1f] text-gray-700 font-medium rounded-t-lg sm:rounded-t-[16px] px-1 py-1">
        <div className="p-4 sm:p-5 md:p-[20px] text-base sm:text-lg rounded-t-[8px] sm:rounded-t-[12px] font-semibold text-gray-700 bg-[#F153291F] border-b border-black pb-2">
          <div className="flex items-center justify-between">
            <h2 className="text-base sm:text-lg md:text-xl font-semibold">
              History & Gate Pass
            </h2>
            <span className="text-xs sm:text-sm font-bold text-black">
              Total Gate Passes: {gatePasses.length}
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-2 sm:space-y-3 px-3 sm:px-4 md:px-5 py-3 sm:py-4">
        {/* Filter Navigation */}
        <div className="border-b border-gray-200">
          {/* Filter Tabs and Search */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4 py-3 sm:py-4">
          {/* Filter Tabs */}
          <nav className="flex flex-nowrap gap-1 sm:gap-1.5 md:gap-2 overflow-x-auto pb-2 lg:pb-0 scrollbar-hide">
            {[
              { key: 'all', label: 'All' },
              { key: 'issued', label: 'Issued' },
              { key: 'pending', label: 'Pending' },
              { key: 'active', label: 'Active' },
              { key: 'expired', label: 'Expired' },
              { key: 'used', label: 'Used' },
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => setFilterStatus(filter.key)}
                className={`px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 rounded-full font-medium text-[10px] sm:text-[11px] md:text-xs transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                  filterStatus === filter.key
                    ? 'bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                }`}
              >
                <span>{filter.label}</span>
                <span className="ml-1">
                  (
                  {
                    gatePasses.filter((gp) => {
                      const status = gp.status?.toLowerCase() || gp.gatePassStatus?.toLowerCase()
                      return filter.key === 'all' || status === filter.key
                    }).length
                  }
                  )
                </span>
              </button>
            ))}
          </nav>

          {/* Search Field */}
          <div className="flex items-center w-full lg:w-auto gap-2.5 sm:gap-3">
            <div className="relative flex-1 lg:flex-none">
              <input
                type="text"
                placeholder="Search by Lot Number, Vehicle Name, or Gate Pass Number"
                className="w-full sm:w-64 md:w-80 px-4 sm:px-5 py-2 sm:py-2.5 md:py-3 pl-10 sm:pl-12 border border-gray-300 rounded-[8px] sm:rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-xs sm:text-sm"
                value={searchTerm || ''}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <svg
                className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-orange-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="p-2 sm:p-2.5 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                title="Clear search"
              >
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Gate Passes List */}
      <div className="space-y-2 sm:space-y-3">
        {filteredGatePasses.length === 0 ? (
          <div className="text-center py-8 sm:py-12 px-4">
            <FileText className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1.5 sm:mb-2">
              No gate passes found
            </h3>
            <p className="text-sm sm:text-base text-gray-500">
              {filterStatus === 'all'
                ? "You don't have any gate passes yet."
                : `No ${filterStatus} gate passes found.`}
            </p>
          </div>
        ) : (
          <div>
            <p>Gate passes</p>
          </div>
        )}
      </div>
      </div>
    </div>
  )
}

export default HistoryAndGatepass
